import { emptyCellJson } from "@/lib/griddSeed";
import type { GriddSlideDocument } from "@gridd/library-embed/react";
import type { GriddCell, GriddDocument } from "@/types/document";

// §8.5 Data compatibility: turn a library slide's Gridd body into a valid `GriddDocument`
// the studio can store and edit.
//
// The two shapes share keys (`cols/rows/cells/borders/cellStyles/merged/spacerStyles`), but
// the library payload is loosely typed and may arrive partial, so this adapter is the seam
// where it becomes trustworthy — the same defensiveness as `griddSeed.ts`:
//   §8.5.1 fill any missing optional (`merged: []`, `borders: []`, `cellStyles: {}`,
//          normalize each cell to `{ id, text, json }`) so a partial payload still yields a
//          structurally valid grid rather than a half-broken one.
//   §8.5.2 regenerate every cell's UUID and remap `cellStyles` in lockstep, so a copy can
//          never collide ids with another embed (the styles map keys off the cell UUID).
//   §8.5.3 Stylesheet interaction: library grids reference colors/presets by NAME, so once
//          copied they restyle under the deck's committed stylesheet like any other grid —
//          *if* the library's preset/color names match the deck's. When they don't, the grid
//          keeps its baked values until re-baked. A full theme-reconcile is out of scope for
//          v1; this adapter deliberately does not touch style names, only cell ids.

function toNumberArray(input: unknown): number[] {
  if (!Array.isArray(input)) return [];
  // Weights are positive numbers; a non-numeric entry falls back to an equal-weight 1
  // rather than poisoning the array with NaN.
  return input.map((v) =>
    typeof v === "number" && Number.isFinite(v) ? v : 1,
  );
}

function asArray(input: unknown): unknown[] {
  return Array.isArray(input) ? input : [];
}

function asRecord(input: unknown): Record<string, unknown> {
  return input && typeof input === "object" && !Array.isArray(input)
    ? (input as Record<string, unknown>)
    : {};
}

// Normalize one incoming cell, minting a fresh id. Returns the new cell plus the id remap
// entry (old→new) when the source carried a string id, so `cellStyles` can follow.
function adaptCell(raw: unknown): { cell: GriddCell; oldId: string | null } {
  const src =
    raw && typeof raw === "object" ? (raw as Record<string, unknown>) : {};
  const oldId = typeof src.id === "string" ? src.id : null;
  return {
    cell: {
      id: crypto.randomUUID(), // §8.5.2 always a fresh id, even if the source had none
      text: typeof src.text === "string" ? src.text : "",
      json: src.json ?? emptyCellJson(),
    },
    oldId,
  };
}

/**
 * Adapt a library slide's Gridd body into a collision-free `GriddDocument`.
 * Pure: no dispatch, no id reuse across calls. The caller mints the `docId` and stores the
 * result via `griddDocCreated` (§8.4.2).
 */
export function libraryDocToGriddDocument(
  input: GriddSlideDocument | null | undefined,
): GriddDocument {
  const src = asRecord(input);

  // §8.5.2 Rebuild the cell grid with fresh ids, recording old→new so styles can be remapped.
  const idMap = new Map<string, string>();
  const cells: GriddCell[][] = asArray(src.cells).map((row) =>
    asArray(row).map((rawCell) => {
      const { cell, oldId } = adaptCell(rawCell);
      if (oldId) idMap.set(oldId, cell.id);
      return cell;
    }),
  );

  // §8.5.2 Remap cellStyles keys in lockstep. A key that matches a copied cell's old id
  // moves to its new id; any other key is kept verbatim (defensive — never drop styling).
  const cellStyles: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(asRecord(src.cellStyles))) {
    cellStyles[idMap.get(key) ?? key] = value;
  }

  const doc: GriddDocument = {
    cols: toNumberArray(src.cols),
    rows: toNumberArray(src.rows),
    cells,
    cellStyles,
    borders: asArray(src.borders),
    merged: asArray(src.merged),
  };
  // spacerStyles is optional in both shapes — only carry it through when present.
  if (src.spacerStyles && typeof src.spacerStyles === "object") {
    doc.spacerStyles = asRecord(src.spacerStyles);
  }
  return doc;
}
